import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  EventEmitter,
  input,
  Output,
  ViewChild,
} from '@angular/core';
import {FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {Image} from '@sportngin/se-commerce-sdk/models/clare';
import {SeFeAlertModule} from 'se-fe-alert';
import {SeFeApiModule} from 'se-fe-api';
import {SeFeBannerModule} from 'se-fe-banner';
import {SeFeButtonModule} from 'se-fe-button';
import {SeFeDomainEnvironmentModule} from 'se-fe-domain-environment';
import {SeFeFormFieldModule} from 'se-fe-form-field';
import {SeFeFormFieldMultiselectModule} from 'se-fe-form-field-multiselect';
import {SeFeFormFieldTextModule} from 'se-fe-form-field-text';
import {SeFeFormServiceModule} from 'se-fe-form-service';
import {SeFeGridModule} from 'se-fe-grid';
import {SeFeIconModule} from 'se-fe-icon';
import {SeFeImageUploaderComponent, SeFeImageUploaderModule} from 'se-fe-image-uploader';
import {SeFeModalComponent, SeFeModalModule} from 'se-fe-modal';
import {SeFeSpinnerModule} from 'se-fe-spinner';
import {SeFeTestIdModule} from 'se-fe-test-id';
import {SeFeTooltipModule} from 'se-fe-tooltip';
import {
  ProductImagesUpdate,
  UpdateProductDetail,
} from '../../../../features/product/components/product-detail/product-detail.store';
import {Product} from '../../../../models/clare';
import * as ImageFormUtils from '../../../../utils/image-form-utils';
import {MultiImageUploadFieldComponent} from '../../multi-image-upload-field/multi-image-upload-field.component';

@Component({
  selector: 'item-media-modal',
  imports: [
    MultiImageUploadFieldComponent,
    ReactiveFormsModule,
    SeFeAlertModule,
    SeFeApiModule,
    SeFeBannerModule,
    SeFeButtonModule,
    SeFeDomainEnvironmentModule,
    SeFeFormFieldModule,
    SeFeFormFieldMultiselectModule,
    SeFeFormFieldTextModule,
    SeFeFormServiceModule,
    SeFeGridModule,
    SeFeIconModule,
    SeFeImageUploaderModule,
    SeFeModalModule,
    SeFeSpinnerModule,
    SeFeTestIdModule,
    SeFeTooltipModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './item-media-modal.component.html',
  styleUrl: './item-media-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class ItemMediaModalComponent {
  @ViewChild('mediaModal') mediaModal: SeFeModalComponent;
  @ViewChild('imageUploader') imageUploader?: SeFeImageUploaderComponent;
  @ViewChild(MultiImageUploadFieldComponent) multiImageUploadField?: MultiImageUploadFieldComponent;

  @Output() save = new EventEmitter<UpdateProductDetail<ProductImagesUpdate>>();
  @Output() uploadFailed = new EventEmitter<void>();
  @Output() uploadSucceeded = new EventEmitter<void>();

  product = input<Product>();
  error = input<string | null>(null);
  /** When true (bundle), allow multiple images; when false (product), allow only one. */
  allowMultipleImages = input<boolean>(false);
  allowMultiple = computed(() => !!this.allowMultipleImages());

  /** First image for default preview in uploader and card */
  image = computed(() => {
    const images = this.product()?.display_metadata?.images;
    return images?.length ? images[0] : {src: '', alt: ''};
  });

  /** Snapshot of product images when modal opened (for reset) */
  originalImages: Image[] = [];

  currentImageUrl = '';
  showImageUploader = true;
  imagesReady = false;
  isSaving = false;
  ignoreNextUploadCleared = false;
  readonly mediaModalAltValidators = [Validators.maxLength(200)];

  form = new FormGroup({
    images: new FormArray<ImageFormUtils.ImageFormGroup>([]),
  });

  get productImageLabel(): {label: string; required: boolean} {
    return {
      label: this.allowMultiple() ? 'Bundle Images' : 'Product Image',
      required: false,
    };
  }

  readonly getImageDescriptionConfig = ImageFormUtils.getImageDescriptionConfig;

  get imagesFormArray(): FormArray<ImageFormUtils.ImageFormGroup> {
    return this.form.get('images') as FormArray<ImageFormUtils.ImageFormGroup>;
  }

  constructor(private cdr: ChangeDetectorRef) {
    let lastRebuildToken = 0;

    effect(() => {
      const images = this.product()?.display_metadata?.images ?? [];
      const nextOriginalImages = images.length ? [...images] : [];
      this.originalImages = nextOriginalImages;
      this.currentImageUrl = nextOriginalImages[0]?.src ?? '';
      // Important: do not mutate `imagesReady` / rebuild form controls inside the same
      // change-detection tick that the parent uses to update inputs. Those synchronous
      // mutations can still trigger NG0100 in dev-mode.
      //
      // However, if we keep old controls visible while `product.display_metadata.images` changes, the
      // se-fe-form-field wrapper can still assert based on validity transitions.
      // So we synchronously gate the template off by flipping `imagesReady` immediately,
      // then rebuild controls asynchronously.
      this.imagesReady = false;
      this.imagesFormArray.clear();

      const rebuildToken = ++lastRebuildToken;
      setTimeout(() => {
        if (rebuildToken !== lastRebuildToken) return;
        this.rebuildImagesFormArray(nextOriginalImages);

        // Second: set flag so template can render; third: trigger CD in a separate macrotask
        // so se-fe-form-field sees stable control validity in one pass (avoids NG0100).
        setTimeout(() => {
          if (rebuildToken !== lastRebuildToken) return;
          this.imagesReady = true;
          setTimeout(() => {
            if (rebuildToken !== lastRebuildToken) return;
            this.cdr.markForCheck();
          }, 0);
        }, 0);
      }, 0);
    });
  }

  getSrcControl(formGroup: FormGroup): FormControl<string> {
    return formGroup.get('src') as FormControl<string>;
  }

  getSrcControlValue(formGroup: FormGroup): string {
    return this.getSrcControl(formGroup).value ?? '';
  }

  getAltControl(formGroup: FormGroup): FormControl<string> {
    return formGroup.get('alt') as FormControl<string>;
  }

  getAltControlValue(formGroup: FormGroup): string {
    return this.getAltControl(formGroup).value ?? '';
  }

  /** Removes the single product image row in non-bundle edit mode. */
  removeImage(index: number): void {
    if (index < 0 || index >= this.imagesFormArray.length) {
      return;
    }

    this.imagesFormArray.removeAt(index);
    this.syncCurrentImageUrl();
    if (!this.currentImageUrl) {
      this.imageUploader?.reset();
    }
    this.imagesFormArray.updateValueAndValidity({emitEvent: false});
    this.form.updateValueAndValidity({emitEvent: false});
    this.cdr.markForCheck();
  }

  onMultiImageUploadSucceeded(): void {
    this.imagesReady = true;
    this.syncCurrentImageUrl();
    this.uploadSucceeded.emit();
    this.cdr.markForCheck();
  }

  onMultiImagesChanged(): void {
    this.syncCurrentImageUrl();
    this.cdr.markForCheck();
  }

  openModal(trigger: unknown): void {
    this.mediaModal.open(trigger);
  }

  closeModal(): void {
    this.resetForm();
    this.mediaModal.close();
  }

  saveDisabled(): boolean {
    return this.isSaving || !this.imagesReady || this.hasUploadInProgress();
  }

  private hasUploadInProgress(): boolean {
    return (
      !!this.multiImageUploadField?.hasUploadInProgress() ||
      !!this.imageUploader?.uploadedItems?.some(item => item.isLoading)
    );
  }

  handleAction(): void {
    const images: Image[] = [];
    let valid = true;
    for (let i = 0; i < this.imagesFormArray.length; i++) {
      const group = this.imagesFormArray.at(i);
      const src = (group.get('src')?.value ?? '').trim();
      const alt = (group.get('alt')?.value ?? '').trim();
      if (src && !alt) {
        // Enforce required at Save-time while keeping init validation stable.
        const altControl = group.get('alt');
        altControl?.setErrors({required: true});
        altControl?.markAsTouched();
        altControl?.markAsDirty();
        valid = false;
      }
      if (src) {
        images.push({src, alt: alt || ''});
      }
    }
    if (!valid) {
      this.cancelSaveState();
      return;
    }

    this.isSaving = true;
    this.cdr.markForCheck();

    this.save.emit({
      data: {images},
      modal: this.getModalController(),
    });
  }

  private getModalController(): SeFeModalComponent {
    return {
      close: () => {
        this.cancelSaveState();
        this.mediaModal.close();
      },
      cancelSpinner: () => {
        this.cancelSaveState();
      },
    } as SeFeModalComponent;
  }

  private cancelSaveState(): void {
    this.isSaving = false;
    this.cdr.markForCheck();
  }

  /** Handles successful single-image upload in product edit mode. */
  onImageSuccess(productImage: {url: string} | {url: string}[]): void {
    if (this.allowMultiple()) {
      return;
    }

    const list = Array.isArray(productImage) ? productImage : [productImage];
    const urls = list.map(item => item?.url ?? '').filter(Boolean);
    if (urls.length === 0) {
      this.resetImageUploaderAfterSuccess();
      return;
    }

    const uploadAccepted = ImageFormUtils.appendUniqueUploadedImages(
      this.imagesFormArray,
      [urls[0]],
      '',
      this.createImageFormGroup.bind(this),
      true
    );
    this.imagesReady = true;
    this.syncCurrentImageUrl();
    if (uploadAccepted) {
      this.resetImageUploaderAfterSuccess();
      this.uploadSucceeded.emit();
    }
    this.cdr.markForCheck();
  }

  onImageFailure(): void {
    this.uploadFailed.emit();
  }

  uploadCleared(): void {
    const shouldClearSingleImage = !this.ignoreNextUploadCleared && !this.allowMultiple();
    this.ignoreNextUploadCleared = false;

    if (shouldClearSingleImage) {
      this.imagesFormArray.clear();
      this.form.updateValueAndValidity({emitEvent: false});
    }

    this.syncCurrentImageUrl();
    this.imagesReady = true;
    this.cdr.markForCheck();
  }

  /**
   * Resets the image uploader after a successful image upload.
   * This is used to clear the uploader and reset the form array.
   */
  private resetImageUploaderAfterSuccess(): void {
    if (!this.imageUploader?.reset) {
      return;
    }

    this.ignoreNextUploadCleared = true;
    this.imageUploader.reset();
    queueMicrotask(() => {
      this.ignoreNextUploadCleared = false;
    });
  }

  resetForm(): void {
    this.cancelSaveState();
    this.rebuildImagesFormArray(this.originalImages);
    this.currentImageUrl = this.originalImages[0]?.src ?? '';
    this.showImageUploader = false;
    this.imagesReady = true;
    this.cdr.markForCheck();
    this.multiImageUploadField?.resetUploadAlerts();

    queueMicrotask(() => {
      this.showImageUploader = true;
      if (this.originalImages.length === 0) {
        this.imageUploader?.reset();
      }
      this.cdr.markForCheck();
    });
  }

  private syncCurrentImageUrl(): void {
    this.currentImageUrl = this.imagesFormArray.at(0)?.get('src')?.value ?? '';
  }

  /**
   * Rebuilds the images form array from a list of images.
   */
  private rebuildImagesFormArray(images: Image[]): void {
    ImageFormUtils.replaceImageFormArray(this.imagesFormArray, images, this.createImageFormGroup.bind(this));
    // Ensure FormArray/FormControl validity is settled before the template renders
    // `se-fe-form-field` (which can assert on styling derived from validity).
    this.imagesFormArray.updateValueAndValidity({emitEvent: false});
    this.form.updateValueAndValidity({emitEvent: false});
  }

  /**
   * Creates a form group for one image row with src and alt.
   */
  private createImageFormGroup(src = '', alt = ''): ImageFormUtils.ImageFormGroup {
    // Only validate max length during init; "required" is enforced manually in `handleAction()`.
    return ImageFormUtils.createImageFormGroup(src, alt, [Validators.maxLength(200)]);
  }
}
